import * as Sentry from '@sentry/react'
import { useEffect, useState } from 'react'
import {
	createRoutesFromElements,
	Navigate,
	Route,
	RouterProvider,
} from 'react-router'
import { sentryCreateBrowserRouter } from 'src'
import MainLayout from 'src/components/Layout/MainLayout'
import Spinner from 'src/components/ui/Spinner'
import SupplierTransaction from 'src/pages/SupplierTransaction'
import { useFetchUserInfoQuery } from 'src/redux/api/graphql/userInfoApi'
import { useFetchCustomerDataQuery } from 'src/redux/api/rest/subscriptionApi'
import { checkAuthenticationStatus } from 'src/redux/slices/auth'
import PrivateRoute from 'src/router/PrivateRoute'
import { getBuildDate } from 'src/utils/dateUtil'
import { useAppSelector, useAppThunkDispatch } from 'src/utils/hooks/reduxHooks'
import { useAmplifyVersionCheck } from 'src/utils/hooks/useAmplifyVersionCheck'
import { useVersionCheck } from 'src/utils/hooks/useVersionCheck'
import { makeErrorToast } from 'src/utils/makeToast'
import reactLazyWithRetry from 'src/utils/reactLazyWithRetry'
import { initVersionTestUtils } from 'src/utils/versionCheckTest'
import packageJson from '../package.json'
import withClearCache from './ClearCache'

const Login = reactLazyWithRetry(() => import('src/pages/Auth/Login/Login'))
const SignupCompSuperUser = reactLazyWithRetry(
	() => import('src/pages/Auth/SignupCompSuperUser')
)
const SignupFreelancer = reactLazyWithRetry(
	() => import('src/pages/Auth/SignupFreelancer')
)
const ConfirmCode = reactLazyWithRetry(
	() => import('src/pages/Auth/ConfirmCode')
)
const SignupChoose = reactLazyWithRetry(
	() => import('src/pages/Auth/SignupChoose')
)
const NewPassword = reactLazyWithRetry(
	() => import('src/pages/Auth/NewPassword')
)
const ForgotPassword = reactLazyWithRetry(
	() => import('src/pages/Auth/ForgotPassword')
)
const FrenchApfpQuote = reactLazyWithRetry(
	() => import('src/pages/public/quote/frenchApfp/FrenchApfpPublicQuote')
)
const Clients = reactLazyWithRetry(() => import('src/pages/Clients'))
const ClientTransaction = reactLazyWithRetry(
	() => import('src/pages/ClientTransaction')
)
const Freelancer = reactLazyWithRetry(() => import('src/pages/Freelancer'))
const Projects = reactLazyWithRetry(() => import('src/pages/Projects'))
const ProjectChildren = reactLazyWithRetry(
	() => import('src/pages/ProjectChildren')
)
const SubTeamAdminPage = reactLazyWithRetry(
	() => import('src/pages/SubTeamAdminPage')
)
const QuoteGridPage1 = reactLazyWithRetry(
	() => import('src/pages/QuoteGrid/QuoteGridPage1')
)
const PrepQuotePage = reactLazyWithRetry(
	() => import('src/pages/QuoteGrid/PrepQuotePage')
)
const WrapRecapQuotePage = reactLazyWithRetry(
	() => import('src/pages/QuoteGrid/WrapRecapPage')
)
const PivotRecapPage = reactLazyWithRetry(
	() => import('src/pages/QuoteGrid/PivotRecapPage')
)
const GanttRecapPage = reactLazyWithRetry(
	() => import('src/pages/QuoteGrid/GanttRecapPage')
)
const Contacts = reactLazyWithRetry(() => import('src/pages/Contacts'))
const Suppliers = reactLazyWithRetry(() => import('src/pages/Suppliers'))
const Proposals = reactLazyWithRetry(() => import('src/pages/Proposals'))
const Invoices = reactLazyWithRetry(() => import('src/pages/Invoices'))
const UploadInvoices = reactLazyWithRetry(
	() => import('src/pages/UploadInvoices')
)
const Purchases = reactLazyWithRetry(() => import('src/pages/Purchases'))
const Dashboard = reactLazyWithRetry(() => import('src/pages/Dashboard'))
const PurchaseOrders = reactLazyWithRetry(
	() => import('src/pages/PurchaseOrders')
)
const AccountSettings = reactLazyWithRetry(
	() => import('src/pages/AccountSettings')
)
const TeamSettings = reactLazyWithRetry(() => import('src/pages/TeamSettings'))
const Billing = reactLazyWithRetry(() => import('src/pages/Billing'))

const SupplierGanttChart = reactLazyWithRetry(
	() => import('src/pages/SupplierGanttChart')
)

const SupplierInvoices = reactLazyWithRetry(
	() => import('src/pages/SupplierInvoices')
)

const Banking = reactLazyWithRetry(() => import('src/pages/Banking'))
const SelectBankAccounts = reactLazyWithRetry(
	() => import('src/pages/SelectBankAccounts')
)

const Reporting = reactLazyWithRetry(() => import('src/pages/Reporting'))
const Payments = reactLazyWithRetry(() => import('src/pages/Payments'))
const QuoteList = reactLazyWithRetry(() => import('src/pages/QuoteList'))
const ProjectList = reactLazyWithRetry(() => import('src/pages/ProjectList'))
const UpgradePlanRequired = reactLazyWithRetry(
	() => import('src/pages/UpgradePlanRequired')
)
const SubscriptionSuccessPage = reactLazyWithRetry(
	() => import('src/pages/SubscriptionSuccessPage')
)
const SubscriptionCancelPage = reactLazyWithRetry(
	() => import('src/pages/SubscriptionCancelPage')
)
const CompanyTemplatePage = reactLazyWithRetry(
	() => import('src/pages/CompanyTemplate')
)

// * HOC for clearing cache if build is different
const ClearCacheComponent = withClearCache(MainApp)

function App() {
	return <ClearCacheComponent />
}

function MainApp() {
	console.log('getBuildDate: ', getBuildDate(packageJson.buildDate))

	// Set current build date in window for version checking
	// This is legitimate initialization that sets up global state for version checking
	// and needs to run once on mount to configure the application.
	// biome-ignore lint: Legitimate initialization effect for version checking
	useEffect(() => {
		if (typeof window !== 'undefined') {
			;(window as any).__PACKAGE_BUILD_DATE__ = packageJson.buildDate
		}

		// Initialize test utilities in development
		initVersionTestUtils()
	}, [])

	// Initialize version checking to detect deployment mismatches
	useVersionCheck()

	// Also use Amplify-based version checking for more accurate detection
	useAmplifyVersionCheck()

	const dispatchThunk = useAppThunkDispatch()
	const userLoginStatus = useAppSelector(
		(state) => state.auth.userLoginStatus
	)
	const initialLoginApiStatus = useAppSelector(
		(state) => state.auth.initialLoginApiStatus
	)

	const userInfoId = useAppSelector((state) => state.auth.userInfo?.id)
	const { data: userInfo, isFetching: isFetchingUserInfo } =
		useFetchUserInfoQuery(
			{
				userInfoId: userInfoId!,
			},
			{
				skip: !userLoginStatus || !userInfoId,
			}
		)
	const userGroups = userInfo?.cognitoGroup
	const [userCheck, setUserCheck] = useState(false)
	Sentry.setUser({ email: userInfo?.email || 'No email' })
	useFetchCustomerDataQuery(userInfo?.currentCompanyTenantId!, {
		skip: !userInfo?.currentCompanyTenantId,
	})

	// Checks is user is authenticated as soon as app is loaded; works in offline mode based on cognito token cookies
	useEffect(() => {
		dispatchThunk(checkAuthenticationStatus())
	}, [dispatchThunk])

	useEffect(() => {
		if (
			initialLoginApiStatus === 'idle' ||
			initialLoginApiStatus === 'loading' ||
			!initialLoginApiStatus
		) {
			setUserCheck(false)
		} else if (initialLoginApiStatus === 'succeeded' && !userGroups) {
			makeErrorToast('Error Logging In, Please Try Again')
			setUserCheck(true)
			Sentry.captureException(`error when logging in user:  ${userInfo}`)
		} else {
			setUserCheck(true)
		}
	}, [initialLoginApiStatus])

	function getHomePage() {
		if (userGroups?.includes('freelancer')) {
			return <Navigate to="/freelancer" replace={true} />
		}
		if (userGroups?.includes('subTeamAdmin')) {
			return <Navigate to="/subTeamAdmin" replace={true} />
		}
		return <Navigate to="/clients" replace={true} />
	}

	const router = sentryCreateBrowserRouter(
		createRoutesFromElements(
			<Route path="/" element={<MainLayout />}>
				<Route path="/login" key={'login'} element={<Login />} />
				<Route
					path="/signupsuperuser"
					key="signupsuperuser"
					element={<SignupCompSuperUser />}
				/>
				<Route
					path="/signupfreelancer"
					key="signupfreelancer"
					element={<SignupFreelancer />}
				/>
				<Route
					path="/confirmCode"
					key="confirmCode"
					element={<ConfirmCode />}
				/>
				<Route path="/signupChoose" element={<SignupChoose />} />
				<Route path="/newPassword" element={<NewPassword />} />
				<Route path="/forgotPassword" element={<ForgotPassword />} />
				<Route
					path="/public/quote/frenchApfp/:quoteId"
					element={<FrenchApfpQuote />}
				/>
				<Route path="/*" element={<Navigate to="/" />} />
				{/* All the below routes can only be accessed by authenticated users */}
				{/* //! You need to update groupAccess here when you add a new cognito group */}
				<Route
					index
					element={
						userLoginStatus ? (
							<PrivateRoute
								isAuth={userLoginStatus}
								groupAccess={[
									'companyAdmin',
									'freelancer',
									'support',
									'teamAdmin',
									'subTeamAdmin',
								]}
								userGroups={userGroups}
								path="/"
							>
								{getHomePage()}
							</PrivateRoute>
						) : (
							<Login />
						)
					}
				/>
				<Route
					path="/clients"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={[
								'companyAdmin',
								'support',
								'teamAdmin',
							]}
							userGroups={userGroups}
							path="/clients"
						>
							<Clients />
						</PrivateRoute>
					}
				/>
				<Route
					path="/clients/:clientId"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={[
								'companyAdmin',
								'freelancer',
								'support',
								'teamAdmin',
							]}
							userGroups={userGroups}
							path="/clients/:clientId"
						>
							<Projects />
						</PrivateRoute>
					}
				/>
				<Route
					path="/projects/:projectId"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={[
								'companyAdmin',
								'freelancer',
								'support',
								'teamAdmin',
								'subTeamAdmin',
							]}
							userGroups={userGroups}
							path="/projects/:projectId"
						>
							<ProjectChildren />
						</PrivateRoute>
					}
				/>
				<Route
					path="/quote/v1/:quoteId/budget"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={[
								'companyAdmin',
								'freelancer',
								'support',
								'teamAdmin',
								'subTeamAdmin',
							]}
							userGroups={userGroups}
							path="/quote/v1/:quoteId/budget"
						>
							<QuoteGridPage1 />
						</PrivateRoute>
					}
				/>
				<Route
					path="/supplierInvoices"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={[
								'companyAdmin',
								'teamAdmin',
								'subTeamAdmin',
								'freelancer',
							]}
							userGroups={userGroups}
							path="/supplierInvoices"
							requiresPremium={true}
						>
							<SupplierInvoices />
						</PrivateRoute>
					}
				/>
				<Route
					path="/proposals"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={['companyAdmin']}
							userGroups={userGroups}
							path="/proposals"
						>
							<Proposals />
						</PrivateRoute>
					}
				/>
				<Route
					path="/invoices"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={['companyAdmin']}
							userGroups={userGroups}
							path="/invoices"
						>
							<Invoices />
						</PrivateRoute>
					}
				/>
				<Route
					path="/suppliers"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={['companyAdmin', 'teamAdmin']}
							userGroups={userGroups}
							path="/suppliers"
						>
							<Suppliers />
						</PrivateRoute>
					}
				/>
				<Route
					path="/purchases"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={[
								'companyAdmin',
								'freelancer',
								'support',
							]}
							userGroups={userGroups}
							path="/purchases"
						>
							<Purchases />
						</PrivateRoute>
					}
				/>
				<Route
					path="/purchaseOrders"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={['companyAdmin']}
							userGroups={userGroups}
							path="/purchaseOrders"
						>
							<PurchaseOrders />
						</PrivateRoute>
					}
				/>
				<Route
					path="/dashboard"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={['companySuperUser']}
							userGroups={userGroups}
							path="/dashboard"
						>
							<Dashboard />
						</PrivateRoute>
					}
				/>
				<Route
					path="/accountSettings"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={['companyAdmin']}
							userGroups={userGroups}
							path="/accountSettings"
						>
							<AccountSettings />
						</PrivateRoute>
					}
				/>
				<Route
					path="/teamSettings"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={['companyAdmin', 'companySuperUser']}
							userGroups={userGroups}
							path="/teamSettings"
						>
							<TeamSettings />
						</PrivateRoute>
					}
				/>
				<Route
					path="/supplierGantt"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={['companyAdmin']}
							userGroups={userGroups}
							path="/supplierGantt"
						>
							<SupplierGanttChart />
						</PrivateRoute>
					}
				/>
				<Route
					path="/quote/v1/:quoteId/prep"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={[
								'companyAdmin',
								'freelancer',
								'support',
								'teamAdmin',
								'subTeamAdmin',
							]}
							userGroups={userGroups}
							path="/quote/v1/:quoteId/prep"
						>
							<PrepQuotePage />
						</PrivateRoute>
					}
				/>
				<Route
					path="/quote/v1/:quoteId/wrap"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={[
								'companyAdmin',
								'freelancer',
								'support',
								'teamAdmin',
								'subTeamAdmin',
							]}
							userGroups={userGroups}
							path="/quote/v1/:quoteId/wrap"
						>
							<WrapRecapQuotePage />
						</PrivateRoute>
					}
				/>
				<Route
					path="/quote/v1/:quoteId/pivot"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={[
								'companyAdmin',
								'freelancer',
								'support',
								'teamAdmin',
								'subTeamAdmin',
							]}
							userGroups={userGroups}
							path="/quote/v1/:quoteId/pivot"
						>
							<PivotRecapPage />
						</PrivateRoute>
					}
				/>
				<Route
					path="/quote/v1/:quoteId/gantt"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={[
								'companyAdmin',
								'freelancer',
								'support',
								'teamAdmin',
								'subTeamAdmin',
							]}
							userGroups={userGroups}
							path="/quote/v1/:quoteId/gantt"
						>
							<GanttRecapPage />
						</PrivateRoute>
					}
				/>
				<Route
					path="/contacts"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={['companyAdmin']}
							userGroups={userGroups}
							path="/contacts"
						>
							<Contacts />
						</PrivateRoute>
					}
				/>
				<Route
					path="/clientTransaction/:clientId"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={['companyAdmin']}
							userGroups={userGroups}
							path="/clientTransaction/:clientId"
						>
							<ClientTransaction />
						</PrivateRoute>
					}
				/>
				<Route
					path="/supplierTransaction/:supplierId"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={['companyAdmin']}
							userGroups={userGroups}
							path="/supplierTransaction/:supplierId"
						>
							<SupplierTransaction />
						</PrivateRoute>
					}
				/>
				<Route
					path="/ui"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={['companyAdmin']}
							userGroups={userGroups}
							path="/ui"
						>
							<UploadInvoices />
						</PrivateRoute>
					}
				/>
				<Route
					path="/banking"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={['companySuperUser']}
							userGroups={userGroups}
							path="/banking"
							requiresPremium={true}
						>
							<Banking />
						</PrivateRoute>
					}
				/>
				<Route
					path="/selectbankaccounts"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={['companySuperUser']}
							userGroups={userGroups}
							path="/selectbankaccounts"
							requiresPremium={true}
						>
							<SelectBankAccounts />
						</PrivateRoute>
					}
				/>
				<Route
					path="/reporting"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={['companySuperUser']}
							userGroups={userGroups}
							path="/reporting"
							requiresPremium={true}
						>
							<Reporting />
						</PrivateRoute>
					}
				/>
				<Route
					path="/payments"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={['companyAdmin']}
							userGroups={userGroups}
							path="/payments"
						>
							<Payments />
						</PrivateRoute>
					}
				/>
				<Route
					path="/quotelist"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={[
								'companyAdmin',
								'teamAdmin',
								'subTeamAdmin',
							]}
							userGroups={userGroups}
							path="/quotelist"
						>
							<QuoteList />
						</PrivateRoute>
					}
				/>
				<Route
					path="/projectlist"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={[
								'companyAdmin',
								'teamAdmin',
								'subTeamAdmin',
							]}
							userGroups={userGroups}
							path="/projectlist"
						>
							<ProjectList />
						</PrivateRoute>
					}
				/>
				<Route
					path="/freelancer"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={['freelancer']}
							userGroups={userGroups}
							path="/freelancer"
						>
							<Freelancer />
						</PrivateRoute>
					}
				/>
				<Route
					path="/subTeamAdmin"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={['subTeamAdmin']}
							userGroups={userGroups}
							path="/subTeamAdmin"
						>
							<SubTeamAdminPage />
						</PrivateRoute>
					}
				/>
				<Route
					path="/success"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={['companySuperUser']}
							userGroups={userGroups}
							path="/success"
						>
							<SubscriptionSuccessPage />
						</PrivateRoute>
					}
				/>
				<Route
					path="/cancel"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={['companySuperUser']}
							userGroups={userGroups}
							path="/cancel"
						>
							<SubscriptionCancelPage />
						</PrivateRoute>
					}
				/>
				<Route
					path="/companyTemplate"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={['companyAdmin', 'teamAdmin']}
							userGroups={userGroups}
							path="/companyTemplate"
						>
							<CompanyTemplatePage />
						</PrivateRoute>
					}
				/>
				<Route
					path="/billing"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={['companyAdmin']}
							userGroups={userGroups}
							path="/billing"
						>
							<Billing />
						</PrivateRoute>
					}
				/>
				<Route
					path="/upgradePlan"
					element={
						<PrivateRoute
							isAuth={userLoginStatus}
							groupAccess={[
								'companyAdmin',
								'companySuperUser',
								'teamAdmin',
								'subTeamAdmin',
								'freelancer',
								'support',
							]}
							userGroups={userGroups}
							path="/upgradePlan"
						>
							<UpgradePlanRequired />
						</PrivateRoute>
					}
				/>
			</Route>
		)
	)

	return (
		<>
			{/* Routes are rendered after the initial check for authenticated user is done */}
			{!userCheck || isFetchingUserInfo ? (
				<Spinner />
			) : (
				<RouterProvider router={router} />
			)}
		</>
	)
}

export default App

// Loader function used in react-router 6.4+

// export async function appLoader() {
//   await store.dispatch(checkAuthenticationStatus())
// }
